import {
  MissionKindEnum,
  MissionWorkflowCategoryEnum,
} from '@e-socna/data-layer';

import {
  PATH_COLLABORATION_MISSIONS_NEW,
  PATH_FOLDER_COLLABORATIONS_NEW,
  PATH_FOLDER_MISSIONS_NEW,
} from '../constants/paths.constant';
import { MissionSpaceEnum } from '../enums/mission-space.enum';

export interface MissionWording {
  readonly name: string;
  readonly ofName: string;
  // The name telling the kinds apart, in the titles of the mission forms.
  readonly qualifiedName: string;
  readonly ofQualifiedName: string;
}

// What varies with the kind of a mission on the front: one place to read it
// from, instead of kind branches spread over the screens.
export interface MissionKindProfile {
  // The URL segment the mission is served under within its folder.
  readonly space: MissionSpaceEnum;
  readonly wording: MissionWording;
  readonly workflowCategory: MissionWorkflowCategoryEnum;
  readonly creationPath: string;
  // The crumb of the folder list above the creation page, named like the
  // URL segment of that page.
  readonly folderListCrumb: string;
  // Created, listed and reached through its collaboration rather than its
  // folder.
  readonly isWithinACollaboration: boolean;
  // Groups sub-missions: their pricing, the tree of them, the collaboration
  // space.
  readonly hasSubMissions: boolean;
  // Attached to, detached from or turned into a collaboration.
  readonly canChangeItsCollaboration: boolean;
}

const MISSION_KIND_PROFILES: Readonly<
  Record<MissionKindEnum, MissionKindProfile>
> = {
  [MissionKindEnum.Standard]: {
    space: MissionSpaceEnum.Missions,
    wording: {
      name: 'Mission',
      ofName: 'de la Mission',
      qualifiedName: 'Mission Simple',
      ofQualifiedName: 'de la Mission Simple',
    },
    workflowCategory: MissionWorkflowCategoryEnum.Standard,
    creationPath: PATH_FOLDER_MISSIONS_NEW,
    folderListCrumb: 'missions',
    isWithinACollaboration: false,
    hasSubMissions: false,
    canChangeItsCollaboration: true,
  },
  [MissionKindEnum.Composite]: {
    space: MissionSpaceEnum.Collaborations,
    wording: {
      name: 'Collaboration',
      ofName: 'de la Collaboration',
      qualifiedName: 'Collaboration',
      ofQualifiedName: 'de la Collaboration',
    },
    workflowCategory: MissionWorkflowCategoryEnum.Collaboration,
    creationPath: PATH_FOLDER_COLLABORATIONS_NEW,
    folderListCrumb: 'collaborations',
    isWithinACollaboration: false,
    hasSubMissions: true,
    canChangeItsCollaboration: false,
  },
  // Nested in its collaboration (missionSpacePathOf), "missions" being the
  // space of a sub-mission whose collaboration is not known (generic link).
  [MissionKindEnum.SubMission]: {
    space: MissionSpaceEnum.Missions,
    wording: {
      name: 'Sous-Mission',
      ofName: 'de la Sous-Mission',
      qualifiedName: 'Sous-Mission',
      ofQualifiedName: 'de la Sous-Mission',
    },
    workflowCategory: MissionWorkflowCategoryEnum.SubMission,
    creationPath: PATH_COLLABORATION_MISSIONS_NEW,
    folderListCrumb: 'missions',
    isWithinACollaboration: true,
    hasSubMissions: false,
    canChangeItsCollaboration: true,
  },
};

export const missionKindProfileOf = (
  kind: MissionKindEnum,
): MissionKindProfile => MISSION_KIND_PROFILES[kind];

// The missions of a folder list leaves out the sub-missions, shown within
// their collaboration.
export const hasSubMissions = (kind: MissionKindEnum): boolean =>
  missionKindProfileOf(kind).hasSubMissions;

export const isListedInItsFolder = (kind: MissionKindEnum): boolean =>
  !missionKindProfileOf(kind).isWithinACollaboration;
